import React, { useEffect, useMemo, useState } from "react";
import "tailwindcss/tailwind.css";
import { Empty } from "antd";
import "../../../styles.css";
import Vectorsorting from "../../../assets/Vectorsorting.svg";
import ShimmerLoader from "../../../loading";
import { useTranslation } from "react-i18next";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import CustomPagination from "../../CustomWidgets/CustomPagination";

const InventoryValuationTable = ({ additionalData, pagination }) => {
  const { t } = useTranslation();
  const [flag, setFlag] = useState(false);

  const data = useMemo(() => additionalData || [], [additionalData]);

  useEffect(() => {
    if (additionalData?.length === 0 && !flag) {
      const timer = setTimeout(() => {
        setFlag(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [additionalData, flag]);

  const columns = useMemo(
    () => [
      {
        Header: (
          <div className="text-start" title={t("Date")}>
            {t("Date")}
          </div>
        ),
        accessor: "create_date",
        Cell: ({ row }) => (
          <div className="text-start">
            {row.original.create_date || row.original.date || "-"}
          </div>
        ),
      },
      {
        Header: (
          <div className="text-start" title={t("Marked Quantity")}>
            {t("Marked Quantity")}
          </div>
        ),
        accessor: "mark_qty",
        Cell: ({ row }) => (
          <div className="text-start">{row.original.mark_qty ?? "-"}</div>
        ),
      },
      {
        Header: (
          <div className="text-start" title={t("Reason")}>
            {t("Reason")}
          </div>
        ),
        accessor: "reason",
        Cell: ({ row }) => (
          <div className="text-start">{row.original.reason || "-"}</div>
        ),
      },
      {
        Header: (
          <div className="text-start" title={t("Notes")}>
            {t("Notes")}
          </div>
        ),
        accessor: "notes",
        Cell: ({ row }) => (
          <div className="text-start break-all whitespace-normal">
            {row.original.notes || "-"}
          </div>
        ),
      },
      {
        Header: (
          <div className="text-start" title={t("Marked By")}>
            {t("Marked By")}
          </div>
        ),
        accessor: "user",
        Cell: ({ row }) => (
          <div className="text-start">{row.original.user || "-"}</div>
        ),
      },
      {
        Header: (
          <div className="text-start" title={t("Email")}>
            {t("Email")}
          </div>
        ),
        accessor: "email",
        Cell: ({ row }) => (
          <div className="text-start">{row.original.email || "-"}</div>
        ),
      },
    ],
    [t],
  );

  const {
    headerGroups,
    prepareRow,
    page,
    nextPage,
    setPageSize,
    state: { pageSize },
  } = useTable(
    {
      columns,
      data: data,
      initialState: { pageSize: 10 },
    },
    useSortBy,
    usePagination,
    useRowSelect,
  );

  const [pageIndex, setpageIndex] = useState(1);

  useEffect(() => {
    const pageDetails = {
      limit: pageSize,
      offset: pageSize * (pageIndex - 1) < 0 ? 0 : pageSize * (pageIndex - 1),
    };

    if (pageSize) pagination(pageDetails);
  }, [pageSize, pageIndex]);

  return (
    <div
      className="container flex flex-col mx-auto ms-4 mb-6 mt-4"
      style={{ maxWidth: "100%" }}
    >
      {additionalData?.length === 0 && flag === false ? (
        <div className="h-40 w-[90%] mt-2 ms-4">
          <ShimmerLoader />
        </div>
      ) : (
        <>
          <div>
            <table
              className="font-normal max-w-[100%] sm:w-[97.6%] md:w-[97.6%] lg:w-[97.6%] xl:w-[97.6%] overflow-hidden rounded-lg"
              style={{ tableLayout: "fixed" }}
            >
              <thead>
                {headerGroups.map((headerGroup) => {
                  const { key: headerKey, ...headerGroupProps } =
                    headerGroup.getHeaderGroupProps();
                  return (
                    <tr
                      key={headerKey}
                      {...headerGroupProps}
                      className="bg-[#78CFEC66] text-sm font-semibold overflow-hidden whitespace-nowrap"
                    >
                      {headerGroup.headers.map((column) => {
                        const { key: columnKey, ...columnProps } =
                          column.getHeaderProps(
                            column.getSortByToggleProps(),
                          );
                        return (
                          <th
                            key={columnKey}
                            {...columnProps}
                            className="py-3 whitespace-nowrap"
                            style={{ width: column.width }}
                          >
                            <div className="flex flex-row items-center ms-2 text-sm leading-tight">
                              {column.render("Header")}
                              {column.canSort && (
                                <span className="ms-1">
                                  {column.isSorted ? (
                                    column.isSortedDesc ? (
                                      <img
                                        src={Vectorsorting}
                                        style={{ minHeight: 10, minWidth: 10 }}
                                        alt="vector"
                                      />
                                    ) : (
                                      <img
                                        className="rotate-180"
                                        src={Vectorsorting}
                                        alt="vector"
                                      />
                                    )
                                  ) : (
                                    <img
                                      src={Vectorsorting}
                                      alt="sortable"
                                      style={{
                                        minHeight: 10,
                                        minWidth: 10,
                                        opacity: 0.3,
                                      }}
                                    />
                                  )}
                                </span>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
            </table>
          </div>

          <div
            className="scrollbar-none bg-white"
            style={{ overflowY: "auto", maxHeight: "450px" }}
          >
            <table
              className="font-normal max-w-[100%] sm:w-[97.6%] md:w-[97.6%] lg:w-[97.6%] xl:w-[97.6%] overflow-hidden rounded-lg"
              style={{ tableLayout: "fixed" }}
            >
              <tbody className="bg-[#F7F9FD] text-center overflow-hidden">
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      key={row.id}
                      className="relative hover:bg-white transition duration-300 border-b border-gray-300"
                    >
                      {row.cells.map((cell, index) => (
                        <td
                          key={index}
                          {...cell.getCellProps()}
                          className="p-2 text-[#272728] align-top text-sm font-normal"
                          style={{ width: cell.column.width }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {page.length === 0 && (
                  <tr>
                    <td colSpan={"100%"}>
                      <Empty
                        className="mt-10"
                        description={t("No data available")}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div>
            <CustomPagination
              data={data?.length}
              pageIndex={pageIndex}
              pageCount={page?.length}
              canPreviousPage={pageIndex != 1}
              canNextPage={data?.length == pageSize}
              gotoPage={(page) => {
                nextPage(page);
              }}
              previousPage={() => {
                setpageIndex(pageIndex - 1);
              }}
              nextPage={() => {
                setpageIndex(pageIndex + 1);
              }}
              pageSize={pageSize}
              setPageSize={(pageSize) => {
                setPageSize(pageSize);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default InventoryValuationTable;
